import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Range } from 'rc-slider';
import 'rc-slider/assets/index.css';
import { FormikField } from 'components/common/form/field/FormikField';
import { FormikCustomRenderProps } from 'components/common/form/field/CustomField';
import { SwitchField } from 'components/common/form/field/SwitchField';
import Select from 'components/common/Select/Select';
import { EditLimitationModel } from './EditLimitationModel';
import { Limitation, WeatherType } from 'core/limitation/Limitation';
import { WEATHER_OPTION_I18N_KEYS } from 'core/limitation/weatherOptions';
import { OPERATE } from 'enum/Operate';
import i18n from 'i18n';
import styles from './editLimitation.module.scss';

const RANGE_CONFIG: Record<
  string,
  { min: number; max: number; unit: string; defaultRange: [number, number] }
> = {
  [WeatherType.TEMPERATURE]: {
    min: 0,
    max: 40,
    unit: '°C',
    defaultRange: [0, 40]
  },
  [WeatherType.PROBABILITY_OF_PRECIPITATION]: {
    min: 0,
    max: 100,
    unit: '%',
    defaultRange: [0, 100]
  },
  [WeatherType.RELATIVE_HUMIDITY]: {
    min: 0,
    max: 100,
    unit: '%',
    defaultRange: [0, 100]
  }
};

// 選單與明細標籤共用同一張對照表，兩邊的文案才不會各走各的。
const getSelectOptions = (): Partial<Record<WeatherType, SelectOptions[]>> =>
  Object.fromEntries(
    Object.entries(WEATHER_OPTION_I18N_KEYS).map(([type, byValue]) => [
      type,
      Object.entries(byValue).map(([value, i18nKey]) => ({
        label: i18n.t(i18nKey),
        value
      }))
    ])
  );

const SELECT_TYPES = new Set<WeatherType>([
  WeatherType.UV_INDEX,
  WeatherType.WEATHER_CODE
]);

const parseRangeValue = (
  value: Limitation['value'] | undefined,
  defaultRange: [number, number]
): [number, number] => {
  if (Array.isArray(value) && value.length > 0 && value[0]) {
    const str = value[0].value || value[0].label;
    if (typeof str === 'string') {
      const match = str.match(/(\d+)~(\d+)/);
      if (match) return [parseInt(match[1], 10), parseInt(match[2], 10)];
    }
  }
  if (typeof value === 'string') {
    const match = value.match(/(\d+)~(\d+)/);
    if (match) return [parseInt(match[1], 10), parseInt(match[2], 10)];
  }
  return defaultRange;
};

const toRangeString = (values: number[]): string => `${values[0]}~${values[1]}`;

// Expand options with ','-separated values into multiple {label, value} items (same label)
const expandSelectOptions = (options: SelectOptions[]): SelectOptions[] =>
  options.flatMap((opt) =>
    opt.value
      .toString()
      .split(',')
      .map((v) => ({ label: opt.label, value: v }))
  );

// Consolidate multiple same-label items back into single option with ','-joined values
const consolidateSelectOptions = (
  items: Limitation['value'] | undefined
): SelectOptions[] => {
  if (!Array.isArray(items)) return [];
  const map = new Map<string, string[]>();
  items.forEach((item) => {
    const existing = map.get(item.label) || [];
    existing.push(String(item.value));
    map.set(item.label, existing);
  });
  return Array.from(map.entries()).map(([label, values]) => ({
    label,
    value: values.join(',')
  }));
};

interface WeatherLimitationSectionProps {
  limitationModel: EditLimitationModel;
}

export const WeatherLimitationSection: React.FC<
  WeatherLimitationSectionProps
> = ({ limitationModel }) => {
  const include = useMemo(
    () => limitationModel.limitationValue[OPERATE.INCLUDE] || [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [limitationModel.limitationValue]
  );

  const [activeTypes, setActiveTypes] = useState<Set<WeatherType>>(
    () =>
      new Set(
        (Object.values(WeatherType) as WeatherType[]).filter((t) =>
          include.some((l) => l.type === t)
        )
      )
  );

  useEffect(() => {
    setActiveTypes(
      new Set(
        (Object.values(WeatherType) as WeatherType[]).filter((t) =>
          include.some((l) => l.type === t)
        )
      )
    );
  }, [include]);

  const onChange = useCallback(
    (type: WeatherType, value: Limitation['value']) => {
      const limitationValue = limitationModel.limitationValue;
      if (!limitationValue[OPERATE.INCLUDE]) {
        limitationValue[OPERATE.INCLUDE] = [];
      }
      const idx = limitationValue[OPERATE.INCLUDE].findIndex(
        (l) => l.type === type
      );
      if (idx > -1) {
        limitationValue[OPERATE.INCLUDE][idx].value = value;
      } else {
        limitationValue[OPERATE.INCLUDE].push({ value, type });
      }
      limitationModel.onLimitationChange();
    },
    [limitationModel]
  );

  const onDelete = useCallback(
    (type: WeatherType) => {
      const limitationValue = limitationModel.limitationValue;
      const idx = (limitationValue[OPERATE.INCLUDE] || []).findIndex(
        (l) => l.type === type
      );
      if (idx !== -1) limitationValue[OPERATE.INCLUDE].splice(idx, 1);
      limitationModel.onLimitationChange();
    },
    [limitationModel]
  );

  const handleSwitchChange = useCallback(
    (type: WeatherType, enabled: boolean) => {
      if (!enabled) {
        onDelete(type);
        setActiveTypes((prev) => {
          const next = new Set(prev);
          next.delete(type);
          return next;
        });
      } else {
        const rangeConfig = RANGE_CONFIG[type];
        if (rangeConfig) {
          const rangeStr = toRangeString(rangeConfig.defaultRange);
          onChange(type, [{ label: rangeStr, value: rangeStr }]);
        }
        setActiveTypes((prev) => new Set(Array.from(prev).concat(type)));
      }
    },
    [onChange, onDelete]
  );

  const handleSelectChange = useCallback(
    (type: WeatherType, val: SelectOptions[]) => {
      if (!val || val.length === 0) {
        handleSwitchChange(type, false);
      } else {
        onChange(type, expandSelectOptions(val));
      }
    },
    [handleSwitchChange, onChange]
  );

  const createToggleHandler = useCallback(
    (type: WeatherType) => (e: React.ChangeEvent<HTMLInputElement>) =>
      handleSwitchChange(type, e.target.checked),
    [handleSwitchChange]
  );

  const createSelectChangeHandler = useCallback(
    (type: WeatherType) => (val: SelectOptions[]) =>
      handleSelectChange(type, val),
    [handleSelectChange]
  );

  const renderRangeSlider = useCallback(
    (type: WeatherType) =>
      // eslint-disable-next-line react/display-name
      (_props: FormikCustomRenderProps) => {
        const config = RANGE_CONFIG[type];
        const limitation = include.find((l) => l.type === type);
        const rangeValues = parseRangeValue(
          limitation?.value,
          config.defaultRange
        );
        const marks: Record<number, string> = {
          [config.min]: `${config.min}${config.unit}`,
          [config.max]: `${config.max}${config.unit}`
        };
        if (limitation?.value) {
          rangeValues.forEach((val) => {
            marks[val] = `${val}${config.unit}`;
          });
        }
        const handleRangeChange = (vals: number[]) => {
          const rangeStr = toRangeString(vals);
          onChange(type, [{ label: rangeStr, value: rangeStr }]);
        };
        return (
          <div className={styles.weatherRangeSlider}>
            <Range
              trackStyle={[{ backgroundColor: '#5845a5' }]}
              handleStyle={[
                { borderColor: '#5845a5' },
                { borderColor: '#5845a5' }
              ]}
              min={config.min}
              max={config.max}
              step={1}
              allowCross={false}
              defaultValue={rangeValues}
              marks={marks}
              onChange={handleRangeChange}
            />
          </div>
        );
      },
    [include, onChange]
  );

  const renderSelectField = useCallback(
    (type: WeatherType) =>
      // eslint-disable-next-line react/display-name
      (_props: FormikCustomRenderProps) => (
        <div>
          <Select
            isMulti
            options={getSelectOptions()[type] || []}
            value={consolidateSelectOptions(
              include.find((l) => l.type === type)?.value
            )}
            menuPlacement='auto'
            onChange={createSelectChangeHandler(type)}
          />
        </div>
      ),
    [include, createSelectChangeHandler]
  );

  return (
    <div>
      {(Object.values(WeatherType) as WeatherType[]).map((type) => {
        const isActive = activeTypes.has(type);

        return (
          <div key={type}>
            <SwitchField
              label={i18n.t(`limitation.labels.${type}`)}
              name={type}
              value={isActive}
              onChange={createToggleHandler(type)}
            />
            {isActive && !SELECT_TYPES.has(type) && (
              <FormikField.Custom
                label=''
                name={type}
                render={renderRangeSlider(type)}
              />
            )}
            {isActive && SELECT_TYPES.has(type) && (
              <FormikField.Custom
                label=''
                name={type}
                render={renderSelectField(type)}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};
